import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react'
import { useNewTranslation } from 'hooks'
import { GridColDef, GridRenderCellParams, GridRenderEditCellParams, useGridApiRef } from '@mui/x-data-grid-pro'
import { styled as muiStyled } from '@mui/material/styles'
import { theme } from 'theme'
import { Box, Flex, Text } from 'rebass'
import ButtonSet from 'components/Shared/Button/OldButton/ButtonSet'
import Icon from 'components/Icon/Icon'
import styled from 'particles/themes/styled'
import _ from 'lodash'
import { SimpleStringCell } from './MuiTable/Cell/SimpleStringCell'
import { StyledDataGrid } from './MuiTable'
import { useSelector } from 'react-redux'
import { selectPlatformOptions } from 'state/constants'
import RadioButtonGroup from 'particles/RadioButton/Group'
import { PLATFORM_ENUM } from 'services/api/constants'

const ScrollBox = styled(Box)`
    width: 100%;
    max-height: 70vh;
    overflow-y: auto;
    box-sizing: content-box;
`

const AddRowContainer = styled(Flex)`
    margin-left: ${theme.spacing.xxxs};
    margin-top: ${theme.spacing.xxs};
    &:hover {
        cursor: pointer;
    }
`

const RemoveIcon = styled(Icon.RedMinus)`
    position: absolute;
    right: ${theme.spacing.xxs};
    &:hover {
        cursor: pointer;
    }
`

const ApprovalPopupGrid = muiStyled(StyledDataGrid)`
    & .MuiDataGrid-cell {
        position: relative;
    }
    & .textCenter {
        justify-content: center;
    }
`

const ErrorMessage = styled(Text)`
    font-size: 0.9em;
    margin-left: ${theme.spacing.xxxs};
    margin-top: ${theme.spacing.xxxs};
    color: ${theme.colors.monza};
`

interface StyledInputProps {
    error?: boolean
}

const Input = styled.input<StyledInputProps>`
    height: 100%;
    width: 100%;
    border: none;
    outline: none;
    color: ${(props: StyledInputProps): string => (props.error ? theme.colors.frenchRose : theme.colors.black)};
`

interface RowInputProps {
    onPaste: (setInputValue: any) => (e: any) => void
    onBlur: (value: string) => void
    value: string
    forceUpdate?: boolean
    error?: boolean
}

const RowInput: React.FC<RowInputProps> = ({ onPaste, onBlur, value, forceUpdate, error = false }) => {
    const [inputValue, setInputValue] = useState(value)
    const input = useRef<HTMLInputElement>(null)
    useEffect(() => {
        if (forceUpdate) {
            setInputValue(value)
        }
    }, [forceUpdate])
    return (
        <Input
            ref={input}
            autoFocus
            value={inputValue}
            onChange={(e): void => setInputValue(e.target.value)}
            onPaste={(e): void => e.currentTarget.blur()}
            onPasteCapture={onPaste(setInputValue)}
            onBlur={(): void => onBlur(inputValue)}
            error={error}
            onKeyDown={(event): void => {
                if ((event.keyCode === 13 || event.keyCode === 9) && input.current) {
                    input.current.blur()
                }
            }}
        />
    )
}

interface RowState {
    id: number
    username: string
    errorMessage?: string
    forceUpdate?: boolean
}

interface UsernameSetState {
    [username: string]: number
}

export const DEFAULT_ROW_COUNT = 5
export const MAX_ROW = 200
export const MAX_APPROVE_CHUNK = 80
interface ListApprovalProps {
    close: () => void
    maxRows?: number
    softLimitNumber?: number
    softLimit?: boolean
    softLimitError?: (totalUsername: number) => React.ReactElement | string | null
    onClickNextMove: (data: { usernames: string[]; platform: PLATFORM_ENUM }) => void
    setTotal?: (number: number, isError: boolean) => void
}

const ListApprovalPopUp: React.FC<ListApprovalProps> = ({
    children,
    close,
    maxRows = MAX_ROW,
    softLimitNumber,
    softLimit = false,
    softLimitError,
    onClickNextMove,
    setTotal,
}) => {
    const [t] = useNewTranslation()
    const scrollBoxRef = useRef<HTMLDivElement>()
    const tableRef = useGridApiRef()
    const platforms = useSelector(selectPlatformOptions)
    const platformRadioOptions = useMemo(
        () => platforms.filter(p => !p.exclude_from_profile).map(p => ({ label: p.name, value: p.id })),
        [platforms],
    )
    const [selectedPlatform, setSelectedPlatform] = useState(PLATFORM_ENUM.IG)

    const [rows, setRows] = useState<RowState[]>(() => {
        const result = []
        for (let i = 0; i < DEFAULT_ROW_COUNT; i++) {
            result.push({ id: i + 1, username: '' })
        }
        return result
    })
    const usernameSet = useRef<UsernameSetState>({})

    const [isMaxRowError, setIsMaxRowError] = useState(false)
    const isUsernameValidationError = useMemo(() => {
        let result = false
        for (const item of rows) {
            if (!_.isEmpty(item.errorMessage)) {
                result = true
                break
            }
        }
        return result
    }, [rows])

    const onProceed = useCallback(() => {
        const usernames = rows.filter(item => !_.isEmpty(item.username)).map(item => item.username)
        if (usernames.length > 0) {
            onClickNextMove({ usernames, platform: selectedPlatform })
        }
    }, [rows, selectedPlatform, onClickNextMove])

    const [addRowDispatched, setAddRowDispatched] = useState(0)
    useEffect(() => {
        if (addRowDispatched === 0) return

        setTimeout(() => {
            if (tableRef.current && scrollBoxRef.current) {
                const rowIds = tableRef.current.getSortedRowIds()
                // Mui 5 Datagrid breaks the setCellFocus behavior, need to work around
                tableRef.current.setCellFocus(rowIds[rowIds.length - 1], 'username')
                // need to manually scroll the scrollbox, because the Mui 5 Datagrid has scrollable area in the table body, so the box cannot be scrolled by setCellFocus
                scrollBoxRef.current.scrollTo(0, scrollBoxRef.current.scrollHeight)
            }
        }, 0)
    }, [addRowDispatched])

    const onAddRow = useCallback(
        (username = ''): void => {
            setRows(currentRows => {
                let newId = 0
                if (currentRows.length > 0) {
                    newId = currentRows[currentRows.length - 1].id + 1
                }
                return [...currentRows, { id: newId, username }]
            })

            // Mui 5 Datagrid breaks the setCellFocus behavior from v4, need to work around
            // trigger focus and scroll to new added row
            setAddRowDispatched(times => times + 1)
        },
        [setRows, tableRef],
    )

    const updateUsernameSetCount = useCallback(
        (username: string, count: number): void => {
            if (_.isEmpty(username.trim())) return
            if (!(username in usernameSet.current)) {
                usernameSet.current[username] = 0
            }
            usernameSet.current[username] += count
        },
        [usernameSet],
    )

    const validateDuplicateUsername = useCallback(
        (username: string, array: RowState[]): void => {
            const errorMessage = t('submssion.importList.invalidAccountMulitpleAccountTableStatus')
            if (usernameSet.current[username] > 1) {
                array.map(item => {
                    if (item.username === username) {
                        item.errorMessage = errorMessage
                    }
                    return item
                })
            } else if (usernameSet.current[username] === 1) {
                array.map(item => {
                    if (item.username === username && item.errorMessage === errorMessage) {
                        item.errorMessage = ''
                    }
                    return item
                })
            }
        },
        [usernameSet],
    )

    const onRemoveRow = useCallback(
        (index: number) => (): void => {
            if (index > -1) {
                const currentItem = rows[index]
                updateUsernameSetCount(currentItem.username, -1)
                const array = rows.filter((_, i) => i !== index)
                if (!_.isEmpty(currentItem.errorMessage)) {
                    validateDuplicateUsername(currentItem.username, array)
                }
                setRows(array)
            }
        },
        [rows, setRows, updateUsernameSetCount, validateDuplicateUsername],
    )

    const setArrayUsername = useCallback(
        (index: number, username: string, array: RowState[], forceUpdate = false): void => {
            if (_.isEmpty(username)) {
                return
            }
            const errorMessage = /^[\w\d._]+$/.test(username)
                ? ''
                : t('errorMessage.validation.usernameCharacterRequirement')
            // add one on duplicate counter
            updateUsernameSetCount(username, 1)
            if (index < array.length) {
                // updating username
                // remove one on duplicate counter
                updateUsernameSetCount(array[index].username, -1)
                array[index].username = username
                array[index].errorMessage = errorMessage
                array[index].forceUpdate = forceUpdate
            } else {
                array.push({ id: array[array.length - 1].id + 1, username, errorMessage })
            }
            if (_.isEmpty(errorMessage)) {
                validateDuplicateUsername(username, array)
            }
        },
        [updateUsernameSetCount, validateDuplicateUsername],
    )

    const handlePaste = useCallback(
        (fromIndex: number) =>
            (setInputValue: React.Dispatch<React.SetStateAction<string>>) =>
            (e: React.ClipboardEvent<HTMLInputElement>): void => {
                let currentIndex = fromIndex
                const array = [...rows]
                const pastedText = e.clipboardData.getData('Text')
                const usernames = pastedText.replace('(?m)^[ \t]*\r?\n', '').split(/\r?\n/)

                if (usernames.length + fromIndex > maxRows) {
                    setIsMaxRowError(true)
                    return
                }

                for (const username of usernames) {
                    const trimmedUsername = username.trim()
                    // Skip empty new line
                    if (_.isEmpty(trimmedUsername)) {
                        continue
                    }
                    if (fromIndex === currentIndex) {
                        tableRef.current.setEditCellValue({
                            id: array[currentIndex].id,
                            field: 'username',
                            value: trimmedUsername,
                        })
                        setInputValue(trimmedUsername)
                    }
                    setArrayUsername(currentIndex, trimmedUsername, array, true)
                    currentIndex++
                }
                setIsMaxRowError(false)
                setRows(array)

                e.preventDefault() // To prevent onChange call
            },
        [rows, setArrayUsername, setRows, setIsMaxRowError],
    )

    const columns: GridColDef[] = useMemo(
        () => [
            {
                field: 'id',
                width: 60,
                headerName: ' ',
                align: 'center',
                renderCell: function Input(params: GridRenderCellParams): React.ReactElement {
                    const rowIndex = tableRef.current.getSortedRowIds().indexOf(params.row.id)
                    return <SimpleStringCell {...params} value={{ value: rowIndex + 1 }} />
                },
            },
            {
                field: 'username',
                headerName: t('submission.importList.socialMediaTableLabel'),
                flex: 2,
                editable: true,
                hideSortIcons: true,
                sortable: false,
                renderCell: function CellText(params: GridRenderCellParams): React.ReactElement {
                    const currentItem = params.row
                    return (
                        <SimpleStringCell
                            {...params}
                            value={{
                                value: currentItem.username || ' ',
                                color: !_.isEmpty(currentItem.errorMessage) ? theme.colors.monza : theme.colors.black,
                            }}
                        />
                    )
                },
                renderEditCell: function Input(params: GridRenderEditCellParams): React.ReactElement {
                    const currentItem = params.row
                    const rowIndex = tableRef.current.getSortedRowIds().indexOf(params.row.id)

                    return (
                        <RowInput
                            error={!_.isEmpty(currentItem.errorMessage)}
                            forceUpdate={currentItem.forceUpdate}
                            value={currentItem.username}
                            onBlur={(value: string): void => {
                                setRows(prevValue => {
                                    const array = [...prevValue]
                                    setArrayUsername(rowIndex, value, array)
                                    return array
                                })
                                if (tableRef.current) {
                                    tableRef.current.setEditCellValue({
                                        id: params.row.id,
                                        field: params.colDef.field,
                                        value,
                                    })
                                    tableRef.current.stopCellEditMode({
                                        id: params.row.id,
                                        field: params.colDef.field,
                                    })
                                }
                            }}
                            onPaste={handlePaste(rowIndex)}
                        />
                    )
                },
            },
            {
                field: 'errorMessage',
                flex: 1,
                headerName: ' ',
                renderCell: function CellText(params: GridRenderCellParams): React.ReactElement {
                    const rowIndex = tableRef.current.getSortedRowIds().indexOf(params.row.id)

                    return (
                        <>
                            <SimpleStringCell
                                {...params}
                                value={{ value: params.value || ' ', color: theme.colors.monza }}
                            />
                            <RemoveIcon onClick={onRemoveRow(rowIndex)} data-testid="delete-row" />
                        </>
                    )
                },
            },
        ],
        [rows, onRemoveRow, setArrayUsername],
    )

    const totalUsername = rows.filter(item => !_.isEmpty(item.username)).length
    const isRemainingOverLimit = Boolean(softLimit && softLimitNumber && totalUsername > softLimitNumber)

    useEffect(() => {
        if (setTotal) {
            setTotal(totalUsername, isRemainingOverLimit)
        }
    }, [totalUsername, isRemainingOverLimit])

    return (
        <>
            <ScrollBox ref={scrollBoxRef}>
                <Box my={theme.spacing.xxs}>
                    {children}
                    {/* Select Impot List Platform */}
                    <Text fontSize={theme.newFontSizes.l} mb={theme.newSpacing.xxs}>
                        {t('submission.importList.selectImportPlatformHeader')}
                    </Text>
                    <RadioButtonGroup
                        name="platformOption"
                        options={platformRadioOptions}
                        selectedOption={selectedPlatform}
                        onClick={setSelectedPlatform}
                        size="s"
                    />
                    <Text fontSize="1.2em" mb={theme.spacing.xxs} mt={theme.newSpacing.l}>
                        {t('submission.importList.importListTableHeader').toUpperCase()}
                    </Text>
                    {/* Import List Table */}
                    <ApprovalPopupGrid
                        experimentalFeatures={{ newEditingApi: true }}
                        apiRef={tableRef}
                        rows={rows}
                        columns={columns}
                        hideFooter
                        autoHeight
                        rowHeight={65}
                        showCellRightBorder
                        showColumnRightBorder
                        disableSelectionOnClick
                        disableColumnResize
                        disableColumnMenu
                        disableVirtualization
                    />
                </Box>
            </ScrollBox>
            {isUsernameValidationError ? (
                <ErrorMessage>{t('errorMessage.validation.fixErrorHighlighted')}</ErrorMessage>
            ) : null}
            {isMaxRowError ? (
                <ErrorMessage>{t('errorMessage.validation.importPayoutListTwoHundredMax')}</ErrorMessage>
            ) : null}
            {softLimit && isRemainingOverLimit && softLimitError ? (
                <ErrorMessage>{softLimitError(totalUsername)}</ErrorMessage>
            ) : null}
            <Box display="inline-block">
                <AddRowContainer
                    opacity={rows.length >= maxRows ? 0.5 : 1}
                    onClick={(): void => (rows.length < maxRows ? onAddRow() : undefined)}
                    data-testid="add-one-row"
                >
                    <Icon.GreenAdd />
                    <Text fontSize="0.9em" ml={theme.spacing.xxxs}>
                        {t('button.addRow')}
                    </Text>
                </AddRowContainer>
            </Box>
            <Flex justifyContent="right" mb={theme.spacing.s}>
                <ButtonSet
                    confirmText={t('button.proceed')}
                    cancelText={t('button.cancel')}
                    onConfirm={onProceed}
                    onCancel={close}
                    disabled={isMaxRowError || isUsernameValidationError || (softLimit && isRemainingOverLimit)}
                />
            </Flex>
        </>
    )
}

export default ListApprovalPopUp
